import { usePageRefreshKey } from '../../../context/PageRefreshContext';
import { useState, useMemo, useEffect } from 'react';
import { Icon } from '@iconify/react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { getVendorCategories, getVendorsList, deleteVendor, getVendorBankAccounts, createVendorBankAccount, updateVendorBankAccount, deleteVendorBankAccount, getVendorAmcList, createVendorAmc, updateVendorAmc, deleteVendorAmc, bulkUploadVendors, resetVendorBulkUpload } from '../../../redux/actions/SocietyPortal/VendorAction/VendorAction';
import { getEmployeesDropdown } from '../../../redux/actions/SocietyPortal/EmployeeAction/EmployeeAction';
import { selectedSocietyStorer } from '../../../utils/webStorer';
import Pagination from '../../../components/common/Pagination';
import axiosInstance from '../../../axios/axiosInstance';
import apiEndpoints from '../../../axios/api';
import { resolveContractStatus } from '../../../utils/helpers/contractStatus';

// eslint-disable-next-line no-unused-vars
const _STATUSES = ['Active', 'Inactive'];

const Vendors = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();

    // Get vendor data from Redux store
    const {
        vendorCategoriesData,
        vendorCategoriesLoading,
        vendorsListData,
        vendorsListLoading,
        vendorsPagination,
        deleteVendorLoading,
        vendorBankAccountsData,
        vendorBankAccountsLoading,
        createVendorBankAccountLoading,
        updateVendorBankAccountLoading,
        deleteVendorBankAccountLoading,
        vendorAmcListData,
        vendorAmcListLoading,
        createVendorAmcLoading,
        updateVendorAmcLoading,
        deleteVendorAmcLoading,
        // Bulk Excel upload
        bulkUploadVendorsLoading,
        bulkUploadVendorsResult,
    } = useSelector(state => state.VendorReducers);

    // Employee dropdown for the AMC member picker. Same list used by the
    // complaint-assignee picker; each row carries an `employee_type` string
    // (e.g. "Regular", "Contractual") so we can filter by the type dropdown.
    const {
        employeesDropdownData,
        employeesDropdownLoading
    } = useSelector(state => state.EmployeeReducer || {});

    const [searchQuery, setSearchQuery] = useState('');
    const [filterCategory, setFilterCategory] = useState('all');
    const [filterStatus, setFilterStatus] = useState('all');
    const [viewMode, setViewMode] = useState('grid');
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(8);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [vendorToDelete, setVendorToDelete] = useState(null);
    const [bankDetailsModalOpen, setBankDetailsModalOpen] = useState(false);
    const [selectedVendorForBank, setSelectedVendorForBank] = useState(null);
    const [bankFormMode, setBankFormMode] = useState('none'); // 'none' | 'add' | 'edit'
    const [editingBankAccount, setEditingBankAccount] = useState(null);
    const [bankAccountForm, setBankAccountForm] = useState({ bankName: '', accountNumber: '', ifscCode: '', bankType: 'Saving' });

    // Excel bulk upload state — one modal for the picker, another for the
    // results summary. Separated so a fresh upload doesn't overlay a stale
    // results panel from the previous attempt.
    const [uploadModalOpen, setUploadModalOpen] = useState(false);
    const [uploadFile, setUploadFile] = useState(null);
    const [uploadResultOpen, setUploadResultOpen] = useState(false);

    const handleDownloadTemplate = async () => {
        // Simple blob download — we use axiosInstance so the auth header goes
        // along automatically. Content-Type is set by the server.
        try {
            const res = await axiosInstance.get(apiEndpoints.SOCIETY_VENDOR_UPLOAD_TEMPLATE_API, { responseType: 'blob' });
            const url = URL.createObjectURL(new Blob([res], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }));
            const a = document.createElement('a');
            a.href = url;
            a.download = 'vendor-upload-template.xlsx';
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
        } catch (e) {
            // Fall back to a direct window.open — server may have proxied the
            // download through an auth-cookie path in some deployments.
            window.open(apiEndpoints.SOCIETY_VENDOR_UPLOAD_TEMPLATE_API, '_blank');
        }
    };

    const handleUploadSubmit = () => {
        if (!uploadFile) return;
        const societyId = selectedSocietyStorer.getSocietyId();
        dispatch(bulkUploadVendors({
            file: uploadFile,
            societyId,
            onSuccess: () => {
                setUploadModalOpen(false);
                setUploadFile(null);
                setUploadResultOpen(true);
                // Refresh the list so newly-created vendors appear immediately.
                dispatch(getVendorsList({ page: currentPage, limit: itemsPerPage, society_id: societyId }));
            },
            onError: () => {
                // Keep the picker open on transport-level errors; per-row
                // failures still open the results modal via onSuccess.
            }
        }));
    };

    const handleCloseResults = () => {
        setUploadResultOpen(false);
        dispatch(resetVendorBulkUpload());
    };

    const AMC_EMPTY_FORM = { amcType: '', serviceSchedule: '', amcStartDate: '', amcEndDate: '', expiryReminderDays: '', serviceReminderDays: '', description: '' };
    // Legacy rows have numeric-only `service_schedule` values (0, 4, 6, 12)
    // — junk from when the DB column was INTEGER and MySQL coerced any string
    // to 0. Only strings that look like real codes get shown / hydrated.
    const isValidSchedule = (s) => typeof s === 'string' && s.length > 0 && !/^-?[0-9]+$/.test(s);
    const [amcModalOpen, setAmcModalOpen] = useState(false);
    const [selectedVendorForAmc, setSelectedVendorForAmc] = useState(null);
    const [amcForm, setAmcForm] = useState(AMC_EMPTY_FORM);
    const [amcMembers, setAmcMembers] = useState([]);
    const [amcMemberForm, setAmcMemberForm] = useState({ memberType: 'employee', employeeType: '', employee: '', committeeType: '', committeeMember: '' });
    const [amcSubmitAttempted, setAmcSubmitAttempted] = useState(false);
    const [amcViewMode, setAmcViewMode] = useState('list'); // 'list' | 'add' | 'edit'
    const [editingAmcId, setEditingAmcId] = useState(null);
    const [amcDeleteConfirm, setAmcDeleteConfirm] = useState(null);

    const amcRequiredErrors = {
        amcType: !amcForm.amcType,
        serviceSchedule: !amcForm.serviceSchedule,
        amcStartDate: !amcForm.amcStartDate,
        amcEndDate: !amcForm.amcEndDate,
    };
    const hasAmcErrors = Object.values(amcRequiredErrors).some(Boolean);

    const handleAmcTracker = (vendor) => {
        setSelectedVendorForAmc(vendor);
        setAmcForm(AMC_EMPTY_FORM);
        setAmcMembers([]);
        setAmcMemberForm({ memberType: 'employee', employeeType: '', employee: '', committeeType: '', committeeMember: '' });
        setAmcSubmitAttempted(false);
        setAmcViewMode('list');
        setAmcModalOpen(true);
        const societyId = selectedSocietyStorer.getSocietyId();
        dispatch(getVendorAmcList({ vendorId: vendor.id, societyId }));
        // Populate the Employee picker used by the AMC "assign members" section.
        // Fetched once per modal open; the payload is small (≤1000 rows).
        // Note the camelCase key — the request-builder reads action.data.societyId.
        if (societyId) dispatch(getEmployeesDropdown({ societyId }));
    };

    const handleAmcClose = () => {
        setAmcModalOpen(false);
        setSelectedVendorForAmc(null);
        setAmcSubmitAttempted(false);
        setAmcViewMode('list');
        setEditingAmcId(null);
        setAmcDeleteConfirm(null);
    };

    const handleAmcSave = () => {
        setAmcSubmitAttempted(true);
        if (hasAmcErrors) return;

        const amcPayload = {
            vendorId: selectedVendorForAmc.id,
            amcType: amcForm.amcType,
            amcStartDate: amcForm.amcStartDate,
            amcEndDate: amcForm.amcEndDate,
            serviceSchedule: amcForm.serviceSchedule,
            expiryReminderDays: amcForm.expiryReminderDays,
            serviceReminderDays: amcForm.serviceReminderDays,
            description: amcForm.description,
            employees: amcMembers.filter(m => m.type === 'employee').length > 0 ? JSON.stringify(amcMembers.filter(m => m.type === 'employee')) : null,
            mcMember: amcMembers.filter(m => m.type === 'committee').length > 0 ? JSON.stringify(amcMembers.filter(m => m.type === 'committee')) : null,
            societyId: selectedSocietyStorer.getSocietyId(),
            onSuccess: () => {
                setAmcForm(AMC_EMPTY_FORM);
                setAmcMembers([]);
                setAmcSubmitAttempted(false);
                setAmcViewMode('list');
                setEditingAmcId(null);
                dispatch(getVendorAmcList({ vendorId: selectedVendorForAmc.id, societyId: selectedSocietyStorer.getSocietyId() }));
            }
        };

        if (amcViewMode === 'edit' && editingAmcId) {
            dispatch(updateVendorAmc({ ...amcPayload, amcId: editingAmcId }));
        } else {
            dispatch(createVendorAmc(amcPayload));
        }
    };

    const handleAmcEdit = (amc) => {
        setEditingAmcId(amc.vendor_amc_id);
        setAmcForm({
            amcType: amc.amc_type || '',
            serviceSchedule: isValidSchedule(amc.service_schedule) ? amc.service_schedule : '',
            amcStartDate: amc.amc_starts || '',
            amcEndDate: amc.amc_ends || '',
            expiryReminderDays: amc.expiry_reminder || '',
            serviceReminderDays: amc.service_reminder || '',
            description: amc.description || '',
        });
        // Parse members from JSON
        const employeeMembers = amc.employees ? (typeof amc.employees === 'string' ? JSON.parse(amc.employees) : amc.employees) : [];
        const committeeMembers = amc.mc_member ? (typeof amc.mc_member === 'string' ? JSON.parse(amc.mc_member) : amc.mc_member) : [];
        setAmcMembers([...employeeMembers, ...committeeMembers]);
        setAmcSubmitAttempted(false);
        setAmcViewMode('edit');
    };

    const handleAmcDelete = (amcId) => {
        dispatch(deleteVendorAmc({
            vendorId: selectedVendorForAmc.id,
            amcId,
            societyId: selectedSocietyStorer.getSocietyId(),
            onSuccess: () => {
                setAmcDeleteConfirm(null);
                dispatch(getVendorAmcList({ vendorId: selectedVendorForAmc.id, societyId: selectedSocietyStorer.getSocietyId() }));
            }
        }));
    };

    const handleAmcFieldChange = (key, value) => setAmcForm(prev => ({ ...prev, [key]: value }));

    const handleAddAmcMember = () => {
        const { memberType, employeeType, employee, committeeType, committeeMember } = amcMemberForm;
        if (memberType === 'employee' && employeeType && employee) {
            setAmcMembers(prev => [...prev, { type: 'employee', employeeType, employee }]);
            setAmcMemberForm(prev => ({ ...prev, employeeType: '', employee: '' }));
        } else if (memberType === 'committee' && committeeType && committeeMember) {
            setAmcMembers(prev => [...prev, { type: 'committee', committeeType, committeeMember }]);
            setAmcMemberForm(prev => ({ ...prev, committeeType: '', committeeMember: '' }));
        }
    };

    const handleRemoveAmcMember = (index) => setAmcMembers(prev => prev.filter((_, i) => i !== index));

    // Fetch vendor categories on component mount
    useEffect(() => {
        dispatch(getVendorCategories());
    }, [dispatch]);

    // Fetch vendors list when filters or pagination change
    const refreshKey = usePageRefreshKey();
    useEffect(() => {
        const categoryId = filterCategory !== 'all'
            ? vendorCategoriesData?.find(cat => cat.label === filterCategory)?.category_id
            : '';
        dispatch(getVendorsList({
            page: currentPage,
            limit: itemsPerPage,
            search: searchQuery,
            category_id: categoryId || ''
        }));
    }, [dispatch, currentPage, itemsPerPage, searchQuery, filterCategory, vendorCategoriesData, refreshKey]);

    // Map API response fields to UI-expected fields
    const vendors = useMemo(() => {
        if (!Array.isArray(vendorsListData)) return [];
        return vendorsListData.map(vendor => ({
            id: vendor.vendor_id,
            name: vendor.business_name,
            status: vendor.is_active === 1 ? 'Active' : 'Inactive',
            contactPerson: vendor.contact_person
                || (vendor.user ? `${vendor.user.first_name || ''} ${vendor.user.last_name || ''}`.trim() : ''),
            category: vendor.category?.label || '',
            // The vendor's own columns only. `vendor.user` is the auto-provisioned login
            // account, which gets reused when the derived login email already exists — so
            // falling back to it showed another person's phone/email against the vendor.
            phone: vendor.phone || '',
            email: vendor.email || '',
            address: vendor.address || '',
            rating: vendor.rating || 0,
            servicesCount: vendor.services_count || 0,
            contractStatus: resolveContractStatus(vendor),
            avatarColor: 'tw-bg-blue-500'
        }));
    }, [vendorsListData]);

    const stats = useMemo(() => {
        const activeVendors = vendors.filter(v => v.status === 'Active');
        const totalServices = vendors.reduce((sum, v) => sum + (v.servicesCount || 0), 0);
        const avgRating = vendors.length > 0
            ? (vendors.reduce((sum, v) => sum + (v.rating || 0), 0) / vendors.length).toFixed(1)
            : '0.0';
        return {
            total: vendorsPagination?.total || vendors.length,
            active: activeVendors.length,
            inactive: vendors.filter(v => v.status === 'Inactive').length,
            contractActive: vendors.filter(v => v.contractStatus === 'Active').length,
            contractExpired: vendors.filter(v => v.contractStatus === 'Expired').length,
            totalServices: totalServices,
            avgRating: avgRating,
            categories: [...new Set(vendors.map(v => v.category))].length,
        };
    }, [vendors, vendorsPagination]);

    // Filter by status (client-side since API handles search and category)
    const filteredVendors = useMemo(() => {
        return vendors.filter(vendor => {
            const matchesStatus = filterStatus === 'all' || vendor.status === filterStatus;
            return matchesStatus;
        });
    }, [vendors, filterStatus]);

    // Pagination from API
    const totalPages = vendorsPagination?.totalPages || Math.ceil(filteredVendors.length / itemsPerPage);
    const paginatedVendors = filteredVendors;

    // Reset to page 1 when filters change
    const handleFilterChange = (setter, value) => {
        setter(value);
        setCurrentPage(1);
    };

    const clearFilters = () => {
        setSearchQuery('');
        setFilterCategory('all');
        setFilterStatus('all');
        setCurrentPage(1);
    };

    const hasActiveFilters = searchQuery || filterCategory !== 'all' || filterStatus !== 'all';

    const handleViewVendor = (vendor) => {
        navigate(`/society-portal/vendors/view/${vendor.id}`);
    };

    const handleEditVendor = (vendor) => {
        navigate(`/society-portal/vendors/edit/${vendor.id}`);
    };

    const handleVendorTariff = (vendor) => {
        navigate(`/society-portal/vendors/${vendor.id}/tariffs`);
    };

    const handleDeleteClick = (vendor) => {
        setVendorToDelete(vendor);
        setDeleteModalOpen(true);
    };

    const handleDeleteConfirm = () => {
        if (vendorToDelete) {
            dispatch(deleteVendor({
                vendorId: vendorToDelete.id,
                onSuccess: () => {
                    setDeleteModalOpen(false);
                    setVendorToDelete(null);
                    // Refresh the vendors list
                    const categoryId = filterCategory !== 'all'
                        ? vendorCategoriesData?.find(cat => cat.label === filterCategory)?.category_id
                        : '';
                    dispatch(getVendorsList({
                        page: currentPage,
                        limit: itemsPerPage,
                        search: searchQuery,
                        category_id: categoryId || ''
                    }));
                }
            }));
        }
    };

    const handleDeleteCancel = () => {
        setDeleteModalOpen(false);
        setVendorToDelete(null);
    };

    const EMPTY_BANK_FORM = { bankName: '', accountNumber: '', ifscCode: '', bankType: 'Saving' };

    const handleBankDetails = (vendor) => {
        setSelectedVendorForBank(vendor);
        setBankDetailsModalOpen(true);
        setBankFormMode('none');
        setEditingBankAccount(null);
        setBankAccountForm(EMPTY_BANK_FORM);
        dispatch(getVendorBankAccounts({ vendorId: vendor.id, societyId: selectedSocietyStorer.getSocietyId() }));
    };

    const handleAddBankAccount = () => {
        setBankAccountForm(EMPTY_BANK_FORM);
        setEditingBankAccount(null);
        setBankFormMode('add');
    };

    const handleEditBankAccount = (account) => {
        setBankAccountForm({
            bankName: account.bank_name || '',
            accountNumber: account.account_number || '',
            ifscCode: account.ifsc_code || '',
            bankType: account.bank_type || 'Saving',
        });
        setEditingBankAccount(account);
        setBankFormMode('edit');
    };

    const handleSaveBankAccount = () => {
        const societyId = selectedSocietyStorer.getSocietyId();
        const basePayload = {
            vendorId: selectedVendorForBank.id,
            societyId,
            bankName: bankAccountForm.bankName,
            accountNumber: bankAccountForm.accountNumber,
            ifscCode: bankAccountForm.ifscCode,
            bankType: bankAccountForm.bankType,
            onSuccess: () => {
                dispatch(getVendorBankAccounts({ vendorId: selectedVendorForBank.id, societyId }));
                setBankFormMode('none');
                setEditingBankAccount(null);
                setBankAccountForm(EMPTY_BANK_FORM);
            },
        };
        if (bankFormMode === 'edit') {
            dispatch(updateVendorBankAccount({ ...basePayload, bankAccountId: editingBankAccount.bank_id }));
        } else {
            dispatch(createVendorBankAccount(basePayload));
        }
    };

    const handleDeleteBankAccount = (accountId) => {
        const societyId = selectedSocietyStorer.getSocietyId();
        dispatch(deleteVendorBankAccount({
            vendorId: selectedVendorForBank.id,
            bankAccountId: accountId,
            societyId,
            onSuccess: () => {
                dispatch(getVendorBankAccounts({ vendorId: selectedVendorForBank.id, societyId }));
            },
        }));
    };

    const handleCancelBankForm = () => {
        setBankFormMode('none');
        setEditingBankAccount(null);
        setBankAccountForm(EMPTY_BANK_FORM);
    };

    const handleBankDetailsClose = () => {
        setBankDetailsModalOpen(false);
        setSelectedVendorForBank(null);
        setBankFormMode('none');
        setEditingBankAccount(null);
    };

    // eslint-disable-next-line no-unused-vars
    const _renderStars = (rating) => {
        const fullStars = Math.floor(rating);
        const hasHalf = rating % 1 >= 0.5;
        return (
            <div className="tw-flex tw-items-center tw-gap-0.5">
                {[...Array(5)].map((_, i) => (
                    <Icon
                        key={i}
                        icon={i < fullStars ? 'mdi:star' : (i === fullStars && hasHalf ? 'mdi:star-half-full' : 'mdi:star-outline')}
                        className={i < fullStars || (i === fullStars && hasHalf) ? 'tw-text-amber-400' : 'tw-text-gray-300'}
                    />
                ))}
                <span className="tw-text-xs tw-text-gray-600 tw-ml-1">{rating}</span>
            </div>
        );
    };

    const getContractBadge = (status) => {
        const styles = {
            'Active': 'tw-bg-emerald-100 tw-text-emerald-700',
            'Pending': 'tw-bg-amber-100 tw-text-amber-700',
            'Expired': 'tw-bg-red-100 tw-text-red-700',
        };
        return styles[status] || 'tw-bg-gray-100 tw-text-gray-600';
    };

    const getCategoryIcon = (category) => {
        const icons = {
            'Plumber': 'mdi:pipe-wrench',
            'Electrician': 'mdi:flash',
            'Carpenter': 'mdi:hammer-screwdriver',
            'Painter': 'mdi:format-paint',
            'Cleaner': 'mdi:spray-bottle',
            'Security': 'mdi:shield-check',
            'Gardener': 'mdi:flower',
            'Pest Control': 'mdi:bug',
            'AC Repair': 'mdi:air-conditioner',
            'Water Purifier': 'mdi:water',
            'Catering': 'mdi:silverware-fork-knife',
            'Lift Maintenance': 'mdi:elevator-passenger',
            'Fire Safety': 'mdi:fire-extinguisher',
            'Housekeeping': 'mdi:broom',
            'Other': 'mdi:dots-horizontal-circle',
        };
        return icons[category] || 'mdi:store';
    };
    console.log('paginatedVendors',paginatedVendors)
   
    return (
        <>
            {/* Header */}
            <div className="tw-flex tw-items-center tw-justify-between tw-mb-4">
                <div>
                    <h1 className="tw-text-lg tw-font-semibold tw-text-gray-800">Vendors</h1>
                    <p className="tw-text-xs tw-text-gray-500">Manage society vendors and service providers</p>
                </div>
                <div className="tw-flex tw-items-center tw-gap-2">
                    <button
                        onClick={() => setUploadModalOpen(true)}
                        className="tw-flex tw-items-center tw-gap-1.5 tw-bg-white tw-text-blue-700 tw-border tw-border-blue-200 tw-px-3 tw-py-1.5 tw-rounded-md tw-text-sm tw-font-medium hover:tw-bg-blue-50"
                        title="Bulk upload vendors from Excel"
                    >
                        <Icon icon="mdi:file-excel-outline" /> Upload Excel
                    </button>
                    <button
                        onClick={() => navigate('/society-portal/vendors/add')}
                        className="tw-flex tw-items-center tw-gap-1.5 tw-bg-blue-600 tw-text-white tw-px-3 tw-py-1.5 tw-rounded-md tw-text-sm tw-font-medium hover:tw-bg-blue-700"
                    >
                        <Icon icon="mdi:plus" /> Add Vendor
                    </button>
                </div>
            </div>

            {/* Stats Cards */}
            <div className="tw-grid tw-grid-cols-6 tw-gap-3 tw-mb-4">
                {[
                    { icon: 'mdi:store', color: 'tw-bg-blue-500', value: stats.total, label: 'Total Vendors' },
                    { icon: 'mdi:check-circle', color: 'tw-bg-emerald-500', value: stats.active, label: 'Active Vendors' },
                    { icon: 'mdi:file-document-check', color: 'tw-bg-purple-500', value: stats.contractActive, label: 'Active Contracts' },
                    { icon: 'mdi:file-document-alert', color: 'tw-bg-amber-500', value: stats.contractExpired, label: 'Expired Contracts' },
                    { icon: 'mdi:star', color: 'tw-bg-yellow-500', value: stats.avgRating, label: 'Avg Rating' },
                    { icon: 'mdi:tools', color: 'tw-bg-teal-500', value: stats.totalServices, label: 'Total Services' },
                ].map((stat, i) => (
                    <div key={i} className="tw-bg-white tw-rounded-lg tw-p-3 tw-shadow-sm tw-border tw-border-gray-100 tw-flex tw-items-center tw-gap-2.5">
                        <div className={`tw-w-10 tw-h-10 tw-rounded-lg ${stat.color} tw-flex tw-items-center tw-justify-center tw-flex-shrink-0`}>
                            <Icon icon={stat.icon} className="tw-text-white tw-text-lg" />
                        </div>
                        <div>
                            <p className="tw-text-lg tw-font-bold tw-text-gray-800 tw-leading-none">{stat.value}</p>
                            <p className="tw-text-[11px] tw-text-gray-500">{stat.label}</p>
                        </div>
                    </div>
                ))}
            </div>

            {/* Filter Bar */}
            <div className="tw-bg-white tw-rounded-lg tw-py-2.5 tw-px-3 tw-mb-4 tw-shadow-sm tw-border tw-border-gray-100 tw-flex tw-items-center tw-gap-2.5 tw-flex-wrap">
                <div className="tw-flex-1 tw-min-w-[200px] tw-relative">
                    <Icon icon="mdi:magnify" className="tw-absolute tw-left-2.5 tw-top-1/2 tw--translate-y-1/2 tw-text-gray-400 tw-text-sm" />
                    <input
                        type="text"
                        placeholder="Search by name, contact or email..."
                        value={searchQuery}
                        onChange={(e) => handleFilterChange(setSearchQuery, e.target.value)}
                        className="tw-w-full tw-pl-8 tw-pr-2 tw-py-1.5 tw-border tw-border-gray-200 tw-rounded-md tw-text-sm focus:tw-outline-none focus:tw-border-blue-500"
                    />
                </div>
                <select
                    value={filterCategory}
                    onChange={(e) => handleFilterChange(setFilterCategory, e.target.value)}
                    disabled={vendorCategoriesLoading}
                    className="tw-px-2.5 tw-py-1.5 tw-border tw-border-gray-200 tw-rounded-md tw-text-sm tw-bg-white focus:tw-outline-none focus:tw-border-blue-500 disabled:tw-bg-gray-100 disabled:tw-cursor-not-allowed"
                >
                    <option value="all">{vendorCategoriesLoading ? 'Loading...' : 'All Categories'}</option>
                    {(vendorCategoriesData || []).map(cat => <option key={cat.category_id} value={cat.label}>{cat.label}</option>)}
                </select>
                {/* <select value={filterStatus} onChange={(e) => handleFilterChange(setFilterStatus, e.target.value)} className="tw-px-2.5 tw-py-1.5 tw-border tw-border-gray-200 tw-rounded-md tw-text-sm tw-bg-white focus:tw-outline-none focus:tw-border-blue-500">
                    <option value="all">All Status</option>
                    {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                </select> */}
                {hasActiveFilters && (
                    <button onClick={clearFilters} className="tw-flex tw-items-center tw-gap-1 tw-text-red-500 tw-text-xs tw-font-medium hover:tw-text-red-600">
                        <Icon icon="mdi:close" className="tw-text-sm" /> Clear
                    </button>
                )}
                <div className="tw-flex tw-border tw-border-gray-200 tw-rounded-md tw-overflow-hidden">
                    <button onClick={() => setViewMode('grid')} className={`tw-p-1.5 ${viewMode === 'grid' ? 'tw-bg-blue-600 tw-text-white' : 'tw-bg-white tw-text-gray-500'}`}>
                        <Icon icon="mdi:view-grid" className="tw-text-sm" />
                    </button>
                    <button onClick={() => setViewMode('list')} className={`tw-p-1.5 ${viewMode === 'list' ? 'tw-bg-blue-600 tw-text-white' : 'tw-bg-white tw-text-gray-500'}`}>
                        <Icon icon="mdi:view-list" className="tw-text-sm" />
                    </button>
                </div>
                <span className="tw-text-[11px] tw-text-gray-400 tw-ml-auto">
                    {vendorsListLoading ? 'Loading...' : `Showing ${filteredVendors.length} of ${vendorsPagination?.total || vendors.length}`}
                </span>
            </div>

            {/* Loading State */}
            {vendorsListLoading && (
                <div className="tw-bg-white tw-rounded-lg tw-shadow-sm tw-border tw-border-gray-100 tw-text-center tw-py-12">
                    <Icon icon="mdi:loading" className="tw-text-4xl tw-text-blue-500 tw-animate-spin" />
                    <p className="tw-text-gray-500 tw-mt-2 tw-text-sm">Loading vendors...</p>
                </div>
            )}

            {/* Grid View */}
            {!vendorsListLoading && viewMode === 'grid' && paginatedVendors.length > 0 && (
                <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 lg:tw-grid-cols-3 xl:tw-grid-cols-4 tw-gap-3">
                    {paginatedVendors.map((vendor) => (
                        <div key={vendor.id} className="tw-bg-white tw-rounded-lg tw-shadow-sm tw-border tw-border-gray-100 tw-p-3">
                            {/* Header with Avatar, Name and Status */}
                            <div className="tw-flex tw-items-start tw-justify-between tw-mb-2.5">
                                <div className="tw-flex tw-items-center tw-gap-2.5">
                                    <div className={`tw-w-10 tw-h-10 tw-rounded-lg ${vendor.avatarColor} tw-flex tw-items-center tw-justify-center tw-flex-shrink-0`}>
                                        <Icon icon={getCategoryIcon(vendor.category)} className="tw-text-white tw-text-lg" />
                                    </div>
                                    <div>
                                        <p className="tw-font-semibold tw-text-gray-800 tw-text-sm tw-line-clamp-1">{vendor.name}</p>
                                        <p className="tw-text-[11px] tw-text-gray-500">{vendor.contactPerson}</p>
                                    </div>
                                </div>
                                <span className={`tw-px-2 tw-py-0.5 tw-rounded tw-text-[10px] tw-font-medium ${vendor.status === 'Active' ? 'tw-bg-emerald-100 tw-text-emerald-700' : 'tw-bg-gray-100 tw-text-gray-600'}`}>
                                    {vendor.status}
                                </span>
                            </div>

                            {/* Category & Rating */}
                            <div className="tw-flex tw-items-center tw-justify-between tw-mb-2 tw-pb-2 tw-border-b tw-border-gray-100">
                                <div className="tw-flex tw-items-center tw-gap-1.5 tw-text-xs tw-text-gray-600">
                                    <Icon icon={getCategoryIcon(vendor.category)} className="tw-text-gray-400" />
                                    <span>{vendor.category}</span>
                                </div>
                                {/* {renderStars(vendor.rating)} */}
                            </div>

                            {/* Contact Info */}
                            <div className="tw-space-y-1.5 tw-mb-2">
                                <div className="tw-flex tw-items-center tw-gap-1.5 tw-text-xs tw-text-gray-600">
                                    <Icon icon="mdi:phone-outline" className="tw-text-gray-400 tw-text-sm" />
                                    <span>{vendor.phone}</span>
                                </div>
                                <div className="tw-flex tw-items-center tw-gap-1.5 tw-text-xs tw-text-gray-600">
                                    <Icon icon="mdi:email-outline" className="tw-text-gray-400 tw-text-sm" />
                                    <span className="tw-truncate">{vendor.email}</span>
                                </div>
                                <div className="tw-flex tw-items-start tw-gap-1.5 tw-text-xs tw-text-gray-600">
                                    <Icon icon="mdi:map-marker-outline" className="tw-text-gray-400 tw-text-sm tw-flex-shrink-0 tw-mt-0.5" />
                                    <span className="tw-line-clamp-1">{vendor.address}</span>
                                </div>
                            </div>

                            {/* Contract & Services Info */}
                            <div className="tw-flex tw-items-center tw-justify-between tw-py-2 tw-border-t tw-border-gray-100 tw-text-xs">
                                <div className="tw-flex tw-items-center tw-gap-1.5">
                                    <span className="tw-text-gray-500">Contract:</span>
                                    <span className={`tw-px-1.5 tw-py-0.5 tw-rounded tw-text-[10px] tw-font-medium ${getContractBadge(vendor.contractStatus)}`}>
                                        {vendor.contractStatus}
                                    </span>
                                </div>
                                {/* <div className="tw-flex tw-items-center tw-gap-1">
                                    <Icon icon="mdi:tools" className="tw-text-gray-400" />
                                    <span className="tw-text-gray-600">{vendor.servicesCount} services</span>
                                </div> */}
                            </div>

                            {/* Actions */}
                            <div className="tw-flex tw-items-center tw-justify-end tw-gap-1 tw-pt-2 tw-border-t tw-border-gray-100">
                                <button onClick={() => handleViewVendor(vendor)} className="tw-p-1.5 tw-rounded hover:tw-bg-gray-100 tw-text-gray-400" title="View">
                                    <Icon icon="mdi:eye-outline" />
                                </button>
                                <button onClick={() => handleEditVendor(vendor)} className="tw-p-1.5 tw-rounded hover:tw-bg-gray-100 tw-text-gray-400" title="Edit">
                                    <Icon icon="mdi:pencil-outline" />
                                </button>
                                <button onClick={() => handleVendorTariff(vendor)} className="tw-p-1.5 tw-rounded hover:tw-bg-blue-50 tw-text-gray-400 hover:tw-text-blue-600" title="Tariffs">
                                    <Icon icon="mdi:file-document-edit-outline" />
                                </button>
                                <button onClick={() => handleBankDetails(vendor)} className="tw-p-1.5 tw-rounded hover:tw-bg-green-50 tw-text-gray-400 hover:tw-text-green-600" title="Bank Details">
                                    <Icon icon="mdi:bank-outline" />
                                </button>
                                <button onClick={() => handleAmcTracker(vendor)} className="tw-p-1.5 tw-rounded hover:tw-bg-teal-50 tw-text-gray-400 hover:tw-text-teal-600" title="AMC Tracker">
                                    <Icon icon="mdi:shield-refresh" />
                                </button>
                                <button onClick={() => handleDeleteClick(vendor)} className="tw-p-1.5 tw-rounded hover:tw-bg-red-50 tw-text-gray-400 hover:tw-text-red-500" title="Delete">
                                    <Icon icon="mdi:delete-outline" />
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Grid View Empty State */}
            {!vendorsListLoading && viewMode === 'grid' && paginatedVendors.length === 0 && (
                <div className="tw-bg-white tw-rounded-lg tw-shadow-sm tw-border tw-border-gray-100 tw-text-center tw-py-12">
                    <Icon icon="mdi:store-off" className="tw-text-4xl tw-text-gray-300" />
                    <p className="tw-text-gray-500 tw-mt-2 tw-text-sm">No vendors found</p>
                </div>
            )}

            {/* List View */}
            {!vendorsListLoading && viewMode === 'list' && (
                <div className="tw-bg-white tw-rounded-lg tw-shadow-sm tw-border tw-border-gray-100 tw-overflow-hidden">
                    <table className="tw-w-full tw-text-sm">
                        <thead>
                            <tr className="tw-bg-gray-50 tw-border-b tw-border-gray-100">
                                <th className="tw-text-left tw-px-3 tw-py-2 tw-text-xs tw-font-semibold tw-text-gray-500 tw-uppercase">Vendor</th>
                                <th className="tw-text-left tw-px-3 tw-py-2 tw-text-xs tw-font-semibold tw-text-gray-500 tw-uppercase">Category</th>
                                <th className="tw-text-left tw-px-3 tw-py-2 tw-text-xs tw-font-semibold tw-text-gray-500 tw-uppercase">Contact</th>
                                {/* <th className="tw-text-left tw-px-3 tw-py-2 tw-text-xs tw-font-semibold tw-text-gray-500 tw-uppercase">Rating</th> */}
                                {/* <th className="tw-text-left tw-px-3 tw-py-2 tw-text-xs tw-font-semibold tw-text-gray-500 tw-uppercase">Status</th> */}
                                <th className="tw-text-left tw-px-3 tw-py-2 tw-text-xs tw-font-semibold tw-text-gray-500 tw-uppercase">Contract</th>
                                <th className="tw-text-center tw-px-3 tw-py-2 tw-text-xs tw-font-semibold tw-text-gray-500 tw-uppercase">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {paginatedVendors.map((vendor) => (
                                <tr key={vendor.id} className="tw-border-b tw-border-gray-50 hover:tw-bg-gray-50">
                                    <td className="tw-px-3 tw-py-2">
                                        <div className="tw-flex tw-items-center tw-gap-2">
                                            <div className={`tw-w-8 tw-h-8 tw-rounded-lg ${vendor.avatarColor} tw-flex tw-items-center tw-justify-center`}>
                                                <Icon icon={getCategoryIcon(vendor.category)} className="tw-text-white tw-text-sm" />
                                            </div>
                                            <div>
                                                <p className="tw-font-medium tw-text-gray-800">{vendor.name}</p>
                                                <p className="tw-text-[10px] tw-text-gray-500">{vendor.contactPerson}</p>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="tw-px-3 tw-py-2 tw-text-gray-600">{vendor.category}</td>
                                    <td className="tw-px-3 tw-py-2">
                                        <p className="tw-text-gray-600">{vendor.phone}</p>
                                        <p className="tw-text-[10px] tw-text-gray-500 tw-truncate tw-max-w-[150px]">{vendor.email}</p>
                                    </td>
                                    {/* <td className="tw-px-3 tw-py-2">
                                        {renderStars(vendor.rating)}
                                    </td> */}
                                    {/* <td className="tw-px-3 tw-py-2">
                                        <span className={`tw-px-2 tw-py-0.5 tw-rounded tw-text-[10px] tw-font-medium ${vendor.status === 'Active' ? 'tw-bg-emerald-100 tw-text-emerald-700' : 'tw-bg-gray-100 tw-text-gray-600'}`}>
                                            {vendor.status}
                                        </span>
                                    </td> */}
                                    <td className="tw-px-3 tw-py-2">
                                        <span className={`tw-px-2 tw-py-0.5 tw-rounded tw-text-[10px] tw-font-medium ${getContractBadge(vendor.contractStatus)}`}>
                                            {vendor.contractStatus}
                                        </span>
                                    </td>
                                    <td className="tw-px-3 tw-py-2">
                                        <div className="tw-flex tw-items-center tw-justify-center tw-gap-1">
                                            <button onClick={() => handleViewVendor(vendor)} className="tw-p-1.5 tw-rounded hover:tw-bg-blue-50 tw-text-gray-400 hover:tw-text-blue-600" title="View">
                                                <Icon icon="mdi:eye-outline" />
                                            </button>
                                            <button onClick={() => handleEditVendor(vendor)} className="tw-p-1.5 tw-rounded hover:tw-bg-gray-100 tw-text-gray-400" title="Edit">
                                                <Icon icon="mdi:pencil-outline" />
                                            </button>
                                            <button onClick={() => handleVendorTariff(vendor)} className="tw-p-1.5 tw-rounded hover:tw-bg-blue-50 tw-text-gray-400 hover:tw-text-blue-600" title="Tariffs">
                                                <Icon icon="mdi:file-document-edit-outline" />
                                            </button>
                                            <button onClick={() => handleBankDetails(vendor)} className="tw-p-1.5 tw-rounded hover:tw-bg-green-50 tw-text-gray-400 hover:tw-text-green-600" title="Bank Details">
                                                <Icon icon="mdi:bank-outline" />
                                            </button>
                                            <button onClick={() => handleAmcTracker(vendor)} className="tw-p-1.5 tw-rounded hover:tw-bg-teal-50 tw-text-gray-400 hover:tw-text-teal-600" title="AMC Tracker">
                                                <Icon icon="mdi:shield-refresh" />
                                            </button>
                                            <button onClick={() => handleDeleteClick(vendor)} className="tw-p-1.5 tw-rounded hover:tw-bg-red-50 tw-text-gray-400 hover:tw-text-red-500" title="Delete">
                                                <Icon icon="mdi:delete-outline" />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    {paginatedVendors.length === 0 && (
                        <div className="tw-text-center tw-py-8">
                            <Icon icon="mdi:store-off" className="tw-text-3xl tw-text-gray-300" />
                            <p className="tw-text-gray-500 tw-mt-2 tw-text-sm">No vendors found</p>
                        </div>
                    )}
                </div>
            )}

            {/* Pagination */}
            {!vendorsListLoading && filteredVendors.length > 0 && (
                <div className="tw-mt-4">
                    <Pagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        totalItems={vendorsPagination?.total || filteredVendors.length}
                        itemsPerPage={itemsPerPage}
                        onPageChange={setCurrentPage}
                        onItemsPerPageChange={(value) => {
                            setItemsPerPage(value);
                            setCurrentPage(1);
                        }}
                        itemsPerPageOptions={[4, 8, 12, 20]}
                        itemLabel="vendors"
                    />
                </div>
            )}

            {/* Delete Confirmation Modal */}
            {deleteModalOpen && (
                <div className="tw-fixed tw-inset-0 tw-bg-black tw-bg-opacity-50 tw-flex tw-items-center tw-justify-center tw-z-50">
                    <div className="tw-bg-white tw-rounded-lg tw-shadow-xl tw-p-6 tw-max-w-md tw-w-full tw-mx-4">
                        <div className="tw-flex tw-items-center tw-gap-3 tw-mb-4">
                            <div className="tw-w-10 tw-h-10 tw-rounded-full tw-bg-red-100 tw-flex tw-items-center tw-justify-center tw-flex-shrink-0">
                                <Icon icon="mdi:delete-alert" className="tw-text-red-600 tw-text-xl" />
                            </div>
                            <div>
                                <h3 className="tw-text-lg tw-font-semibold tw-text-gray-800">Delete Vendor</h3>
                                <p className="tw-text-sm tw-text-gray-500">This action cannot be undone</p>
                            </div>
                        </div>
                        <p className="tw-text-sm tw-text-gray-600 tw-mb-6">
                            Are you sure you want to delete <span className="tw-font-semibold">{vendorToDelete?.name}</span>?
                            All associated data will be permanently removed.
                        </p>
                        <div className="tw-flex tw-items-center tw-justify-end tw-gap-3">
                            <button
                                onClick={handleDeleteCancel}
                                disabled={deleteVendorLoading}
                                className="tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-gray-600 tw-bg-gray-100 hover:tw-bg-gray-200 tw-rounded-lg disabled:tw-opacity-50"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleDeleteConfirm}
                                disabled={deleteVendorLoading}
                                className="tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-white tw-bg-red-600 hover:tw-bg-red-700 tw-rounded-lg tw-flex tw-items-center tw-gap-2 disabled:tw-opacity-50"
                            >
                                {deleteVendorLoading ? (
                                    <>
                                        <Icon icon="mdi:loading" className="tw-animate-spin" />
                                        Deleting...
                                    </>
                                ) : (
                                    <>
                                        <Icon icon="mdi:delete" />
                                        Delete
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Bank Accounts Modal */}
            {bankDetailsModalOpen && (
                <div className="tw-fixed tw-inset-0 tw-bg-black tw-bg-opacity-50 tw-flex tw-items-center tw-justify-center tw-z-50">
                    <div className="tw-bg-white tw-rounded-lg tw-shadow-xl tw-w-full tw-max-w-lg tw-mx-4 tw-flex tw-flex-col" style={{ maxHeight: '90vh' }}>
                        {/* Modal Header */}
                        <div className="tw-flex tw-items-center tw-justify-between tw-px-5 tw-py-4 tw-border-b tw-border-gray-100">
                            <div className="tw-flex tw-items-center tw-gap-3">
                                <div className="tw-w-9 tw-h-9 tw-rounded-full tw-bg-green-100 tw-flex tw-items-center tw-justify-center tw-flex-shrink-0">
                                    <Icon icon="mdi:bank-outline" className="tw-text-green-600 tw-text-lg" />
                                </div>
                                <div>
                                    <h3 className="tw-text-sm tw-font-semibold tw-text-gray-800">Bank Accounts</h3>
                                    <p className="tw-text-xs tw-text-gray-500">{selectedVendorForBank?.name}</p>
                                </div>
                            </div>
                            <button onClick={handleBankDetailsClose} className="tw-p-1.5 tw-rounded hover:tw-bg-gray-100 tw-text-gray-400">
                                <Icon icon="mdi:close" />
                            </button>
                        </div>

                        {/* Modal Body */}
                        <div className="tw-flex-1 tw-overflow-y-auto tw-px-5 tw-py-4 tw-space-y-3">

                            {/* Loading */}
                            {vendorBankAccountsLoading && (
                                <div className="tw-text-center tw-py-8">
                                    <Icon icon="mdi:loading" className="tw-text-3xl tw-text-blue-500 tw-animate-spin" />
                                    <p className="tw-text-gray-500 tw-mt-2 tw-text-sm">Loading bank accounts...</p>
                                </div>
                            )}

                            {/* Existing bank accounts list */}
                            {!vendorBankAccountsLoading && Array.isArray(vendorBankAccountsData) && vendorBankAccountsData.map((account) => (
                                <div key={account.bank_id} className="tw-border tw-border-gray-200 tw-rounded-lg tw-p-3 tw-bg-gray-50 tw-relative">
                                    <div className="tw-flex tw-items-start tw-justify-between tw-gap-2">
                                        <div className="tw-flex-1 tw-min-w-0">
                                            <div className="tw-flex tw-items-center tw-gap-1.5 tw-mb-1">
                                                <Icon icon="mdi:bank" className="tw-text-gray-500 tw-text-sm tw-flex-shrink-0" />
                                                <p className="tw-text-sm tw-font-medium tw-text-gray-800 tw-truncate">{account.bank_name || '—'}</p>
                                            </div>
                                            <div className="tw-grid tw-grid-cols-2 tw-gap-x-4 tw-gap-y-0.5 tw-text-xs tw-text-gray-600">
                                                <div className="tw-flex tw-items-center tw-gap-1">
                                                    <Icon icon="mdi:credit-card-outline" className="tw-text-gray-400 tw-flex-shrink-0" />
                                                    <span className="tw-truncate">{account.account_number || '—'}</span>
                                                </div>
                                                <div className="tw-flex tw-items-center tw-gap-1">
                                                    <Icon icon="mdi:identifier" className="tw-text-gray-400 tw-flex-shrink-0" />
                                                    <span>{account.ifsc_code || '—'}</span>
                                                </div>
                                                <div className="tw-flex tw-items-center tw-gap-1">
                                                    <Icon icon="mdi:bank-transfer" className="tw-text-gray-400 tw-flex-shrink-0" />
                                                    <span>{account.bank_type || '—'}</span>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="tw-flex tw-gap-1 tw-flex-shrink-0">
                                            <button
                                                onClick={() => handleEditBankAccount(account)}
                                                disabled={deleteVendorBankAccountLoading}
                                                className="tw-p-1.5 tw-rounded hover:tw-bg-blue-50 tw-text-gray-400 hover:tw-text-blue-600 disabled:tw-opacity-40"
                                                title="Edit"
                                            >
                                                <Icon icon="mdi:pencil-outline" className="tw-text-sm" />
                                            </button>
                                            <button
                                                onClick={() => handleDeleteBankAccount(account.bank_id)}
                                                disabled={deleteVendorBankAccountLoading}
                                                className="tw-p-1.5 tw-rounded hover:tw-bg-red-50 tw-text-gray-400 hover:tw-text-red-500 disabled:tw-opacity-40"
                                                title="Delete"
                                            >
                                                {deleteVendorBankAccountLoading
                                                    ? <Icon icon="mdi:loading" className="tw-text-sm tw-animate-spin" />
                                                    : <Icon icon="mdi:delete-outline" className="tw-text-sm" />
                                                }
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ))}

                            {/* Empty state */}
                            {!vendorBankAccountsLoading && (!vendorBankAccountsData || vendorBankAccountsData.length === 0) && bankFormMode === 'none' && (
                                <div className="tw-text-center tw-py-8 tw-text-gray-400">
                                    <Icon icon="mdi:bank-off-outline" className="tw-text-4xl" />
                                    <p className="tw-text-sm tw-mt-2">No bank accounts added yet</p>
                                </div>
                            )}

                            {/* Add / Edit inline form */}
                            {bankFormMode !== 'none' && (
                                <div className="tw-border tw-border-blue-200 tw-rounded-lg tw-p-4 tw-bg-blue-50 tw-space-y-3">
                                    <p className="tw-text-xs tw-font-semibold tw-text-blue-700 tw-uppercase tw-tracking-wide">
                                        {bankFormMode === 'add' ? 'New Bank Account' : 'Edit Bank Account'}
                                    </p>
                                    <div className="tw-grid tw-grid-cols-2 tw-gap-3">
                                        <div>
                                            <label className="tw-block tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1">Bank Name</label>
                                            <input
                                                type="text"
                                                value={bankAccountForm.bankName}
                                                onChange={(e) => setBankAccountForm(prev => ({ ...prev, bankName: e.target.value }))}
                                                placeholder="e.g. HDFC Bank"
                                                className="tw-w-full tw-px-2.5 tw-py-1.5 tw-border tw-border-gray-200 tw-rounded-md tw-text-sm focus:tw-outline-none focus:tw-border-blue-500 tw-bg-white"
                                            />
                                        </div>
                                        <div>
                                            <label className="tw-block tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1">Account Number</label>
                                            <input
                                                type="text"
                                                value={bankAccountForm.accountNumber}
                                                onChange={(e) => setBankAccountForm(prev => ({ ...prev, accountNumber: e.target.value }))}
                                                placeholder="Enter account number"
                                                className="tw-w-full tw-px-2.5 tw-py-1.5 tw-border tw-border-gray-200 tw-rounded-md tw-text-sm focus:tw-outline-none focus:tw-border-blue-500 tw-bg-white"
                                            />
                                        </div>
                                        <div>
                                            <label className="tw-block tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1">IFSC Code</label>
                                            <input
                                                type="text"
                                                value={bankAccountForm.ifscCode}
                                                onChange={(e) => setBankAccountForm(prev => ({ ...prev, ifscCode: e.target.value.toUpperCase() }))}
                                                placeholder="e.g. HDFC0001234"
                                                className="tw-w-full tw-px-2.5 tw-py-1.5 tw-border tw-border-gray-200 tw-rounded-md tw-text-sm focus:tw-outline-none focus:tw-border-blue-500 tw-bg-white"
                                            />
                                        </div>
                                        <div>
                                            <label className="tw-block tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1">Account Type</label>
                                            <select
                                                value={bankAccountForm.bankType}
                                                onChange={(e) => setBankAccountForm(prev => ({ ...prev, bankType: e.target.value }))}
                                                className="tw-w-full tw-px-2.5 tw-py-1.5 tw-border tw-border-gray-200 tw-rounded-md tw-text-sm focus:tw-outline-none focus:tw-border-blue-500 tw-bg-white"
                                            >
                                                <option value="Saving">Saving</option>
                                                <option value="Current">Current</option>
                                            </select>
                                        </div>
                                    </div>
                                    <div className="tw-flex tw-justify-end tw-gap-2">
                                        <button
                                            onClick={handleCancelBankForm}
                                            disabled={createVendorBankAccountLoading || updateVendorBankAccountLoading}
                                            className="tw-px-3 tw-py-1.5 tw-text-xs tw-font-medium tw-text-gray-600 tw-bg-white tw-border tw-border-gray-200 hover:tw-bg-gray-50 tw-rounded-md disabled:tw-opacity-50"
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            onClick={handleSaveBankAccount}
                                            disabled={createVendorBankAccountLoading || updateVendorBankAccountLoading}
                                            className="tw-px-3 tw-py-1.5 tw-text-xs tw-font-medium tw-text-white tw-bg-blue-600 hover:tw-bg-blue-700 tw-rounded-md tw-flex tw-items-center tw-gap-1.5 disabled:tw-opacity-50"
                                        >
                                            {(createVendorBankAccountLoading || updateVendorBankAccountLoading) ? (
                                                <><Icon icon="mdi:loading" className="tw-animate-spin" /> Saving...</>
                                            ) : (
                                                <><Icon icon="mdi:content-save-outline" /> Save</>
                                            )}
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Modal Footer */}
                        <div className="tw-px-5 tw-py-3 tw-border-t tw-border-gray-100 tw-flex tw-justify-between tw-items-center">
                            {bankFormMode === 'none' ? (
                                <button
                                    onClick={handleAddBankAccount}
                                    className="tw-flex tw-items-center tw-gap-1.5 tw-text-sm tw-font-medium tw-text-blue-600 hover:tw-text-blue-700"
                                >
                                    <Icon icon="mdi:plus-circle-outline" />
                                    Add Bank Account
                                </button>
                            ) : <span />}
                            <button
                                onClick={handleBankDetailsClose}
                                className="tw-px-4 tw-py-1.5 tw-text-sm tw-font-medium tw-text-gray-600 tw-bg-gray-100 hover:tw-bg-gray-200 tw-rounded-lg"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* AMC Tracker Modal */}
            {amcModalOpen && (
                <div className="tw-fixed tw-inset-0 tw-bg-black tw-bg-opacity-50 tw-flex tw-items-center tw-justify-center tw-z-50">
                    <div className="tw-bg-white tw-rounded-lg tw-shadow-xl tw-w-full tw-max-w-2xl tw-mx-4 tw-flex tw-flex-col" style={{ maxHeight: '90vh' }}>
                        {/* Header */}
                        <div className="tw-flex tw-items-center tw-justify-between tw-px-5 tw-py-4 tw-border-b tw-border-gray-100">
                            <div className="tw-flex tw-items-center tw-gap-3">
                                <div className="tw-w-9 tw-h-9 tw-rounded-full tw-bg-teal-100 tw-flex tw-items-center tw-justify-center tw-flex-shrink-0">
                                    <Icon icon="mdi:shield-refresh" className="tw-text-teal-600 tw-text-lg" />
                                </div>
                                <div>
                                    <h3 className="tw-text-sm tw-font-semibold tw-text-gray-800">AMC Tracker</h3>
                                    <p className="tw-text-xs tw-text-gray-500">{selectedVendorForAmc?.name}</p>
                                </div>
                            </div>
                            <button onClick={handleAmcClose} className="tw-p-1.5 tw-rounded hover:tw-bg-gray-100 tw-text-gray-400">
                                <Icon icon="mdi:close" />
                            </button>
                        </div>

                        {/* Body */}
                        <div className="tw-flex-1 tw-overflow-y-auto tw-px-5 tw-py-4 tw-space-y-4">

                            {/* AMC List View */}
                            {amcViewMode === 'list' && (
                                <>
                                    <div className="tw-flex tw-items-center tw-justify-between tw-mb-2">
                                        <p className="tw-text-xs tw-font-semibold tw-text-gray-700">AMC Records</p>
                                        <button
                                            onClick={() => { setAmcViewMode('add'); setAmcForm(AMC_EMPTY_FORM); setAmcMembers([]); setAmcSubmitAttempted(false); }}
                                            className="tw-flex tw-items-center tw-gap-1.5 tw-bg-teal-600 tw-text-white tw-px-3 tw-py-1.5 tw-rounded-lg tw-text-xs tw-font-medium hover:tw-bg-teal-700"
                                        >
                                            <Icon icon="mdi:plus" /> Add AMC
                                        </button>
                                    </div>

                                    {vendorAmcListLoading ? (
                                        <div className="tw-flex tw-items-center tw-justify-center tw-py-10">
                                            <Icon icon="mdi:loading" className="tw-animate-spin tw-text-teal-500 tw-text-2xl" />
                                            <span className="tw-ml-2 tw-text-sm tw-text-gray-500">Loading AMC records...</span>
                                        </div>
                                    ) : Array.isArray(vendorAmcListData) && vendorAmcListData.length > 0 ? (
                                        <div className="tw-space-y-2">
                                            {vendorAmcListData.map((amc) => (
                                                <div key={amc.vendor_amc_id} className="tw-border tw-border-gray-200 tw-rounded-lg tw-p-3 hover:tw-border-teal-200 hover:tw-bg-teal-50/30 tw-transition-all">
                                                    <div className="tw-flex tw-items-start tw-justify-between">
                                                        <div className="tw-flex-1">
                                                            <div className="tw-flex tw-items-center tw-gap-2 tw-mb-1.5">
                                                                <span className="tw-text-xs tw-font-semibold tw-text-gray-800">{amc.amc_type}</span>
                                                                <span className={`tw-px-2 tw-py-0.5 tw-rounded-full tw-text-[10px] tw-font-medium ${amc.status === 'A' ? 'tw-bg-green-100 tw-text-green-700' : amc.status === 'I' ? 'tw-bg-yellow-100 tw-text-yellow-700' : 'tw-bg-red-100 tw-text-red-700'}`}>
                                                                    {amc.status === 'A' ? 'Active' : amc.status === 'I' ? 'Inactive' : 'Deleted'}
                                                                </span>
                                                            </div>
                                                            <div className="tw-grid tw-grid-cols-2 tw-gap-x-4 tw-gap-y-1 tw-text-xs tw-text-gray-500">
                                                                <span><span className="tw-font-medium tw-text-gray-600">Start:</span> {amc.amc_starts}</span>
                                                                <span><span className="tw-font-medium tw-text-gray-600">End:</span> {amc.amc_ends}</span>
                                                                {isValidSchedule(amc.service_schedule) && <span><span className="tw-font-medium tw-text-gray-600">Schedule:</span> {amc.service_schedule}</span>}
                                                                {amc.expiry_reminder && <span><span className="tw-font-medium tw-text-gray-600">Expiry Reminder:</span> {amc.expiry_reminder} days</span>}
                                                            </div>
                                                            {amc.description && <p className="tw-text-xs tw-text-gray-400 tw-mt-1.5 tw-line-clamp-1">{amc.description}</p>}
                                                        </div>
                                                        <div className="tw-flex tw-items-center tw-gap-1 tw-flex-shrink-0">
                                                            <button
                                                                onClick={() => handleAmcEdit(amc)}
                                                                className="tw-p-1.5 tw-rounded hover:tw-bg-blue-100 tw-text-gray-400 hover:tw-text-blue-600"
                                                                title="Edit AMC"
                                                            >
                                                                <Icon icon="mdi:pencil-outline" className="tw-text-sm" />
                                                            </button>
                                                            {amcDeleteConfirm === amc.vendor_amc_id ? (
                                                                <div className="tw-flex tw-items-center tw-gap-1">
                                                                    <button
                                                                        onClick={() => handleAmcDelete(amc.vendor_amc_id)}
                                                                        disabled={deleteVendorAmcLoading}
                                                                        className="tw-px-2 tw-py-1 tw-rounded tw-text-[10px] tw-font-medium tw-bg-red-600 tw-text-white hover:tw-bg-red-700 disabled:tw-opacity-50"
                                                                    >
                                                                        {deleteVendorAmcLoading ? 'Deleting...' : 'Confirm'}
                                                                    </button>
                                                                    <button
                                                                        onClick={() => setAmcDeleteConfirm(null)}
                                                                        className="tw-px-2 tw-py-1 tw-rounded tw-text-[10px] tw-font-medium tw-bg-gray-100 tw-text-gray-600 hover:tw-bg-gray-200"
                                                                    >
                                                                        Cancel
                                                                    </button>
                                                                </div>
                                                            ) : (
                                                                <button
                                                                    onClick={() => setAmcDeleteConfirm(amc.vendor_amc_id)}
                                                                    className="tw-p-1.5 tw-rounded hover:tw-bg-red-100 tw-text-gray-400 hover:tw-text-red-500"
                                                                    title="Delete AMC"
                                                                >
                                                                    <Icon icon="mdi:trash-can-outline" className="tw-text-sm" />
                                                                </button>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-py-10 tw-text-gray-400">
                                            <Icon icon="mdi:shield-refresh-outline" className="tw-text-4xl tw-mb-2" />
                                            <p className="tw-text-sm">No AMC records found</p>
                                            <p className="tw-text-xs tw-mt-1">Click "Add AMC" to create one</p>
                                        </div>
                                    )}
                                </>
                            )}

                            {/* AMC Add/Edit Form View */}
                            {(amcViewMode === 'add' || amcViewMode === 'edit') && (
                                <>
                                    {/* AMC Fields */}
                                    <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-3">
                                        <div>
                                            <label className="tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1 tw-block">AMC Type *</label>
                                            <select
                                                value={amcForm.amcType}
                                                onChange={(e) => handleAmcFieldChange('amcType', e.target.value)}
                                                className={`tw-w-full tw-px-3 tw-py-2 tw-border tw-rounded-lg tw-text-sm tw-bg-white focus:tw-outline-none ${amcSubmitAttempted && amcRequiredErrors.amcType ? 'tw-border-red-500 focus:tw-border-red-500' : 'tw-border-gray-200 focus:tw-border-teal-500'}`}
                                            >
                                                <option value="">Select AMC Type</option>
                                                <option value="Assets AMC Contracts">Assets AMC Contracts</option>
                                                <option value="Services AMC Contracts">Services AMC Contracts</option>
                                            </select>
                                            {amcSubmitAttempted && amcRequiredErrors.amcType && <p className="tw-text-xs tw-text-red-500 tw-mt-1">AMC Type is required</p>}
                                        </div>
                                        <div>
                                            <label className="tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1 tw-block">Service Schedule *</label>
                                            <select
                                                value={amcForm.serviceSchedule}
                                                onChange={(e) => handleAmcFieldChange('serviceSchedule', e.target.value)}
                                                className={`tw-w-full tw-px-3 tw-py-2 tw-border tw-rounded-lg tw-text-sm tw-bg-white focus:tw-outline-none ${amcSubmitAttempted && amcRequiredErrors.serviceSchedule ? 'tw-border-red-500 focus:tw-border-red-500' : 'tw-border-gray-200 focus:tw-border-teal-500'}`}
                                            >
                                                <option value="">Select Schedule</option>
                                                <option value="Monthly">Monthly</option>
                                                <option value="Bi-Monthly">Bi-Monthly</option>
                                                <option value="Quarterly">Quarterly</option>
                                                <option value="Half-Yearly">Half-Yearly</option>
                                                <option value="Yearly">Yearly</option>
                                                <option value="On-Demand">On-Demand</option>
                                            </select>
                                            {amcSubmitAttempted && amcRequiredErrors.serviceSchedule && <p className="tw-text-xs tw-text-red-500 tw-mt-1">Service Schedule is required</p>}
                                        </div>
                                        <div>
                                            <label className="tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1 tw-block">AMC Start Date *</label>
                                            <input
                                                type="date"
                                                value={amcForm.amcStartDate}
                                                onChange={(e) => handleAmcFieldChange('amcStartDate', e.target.value)}
                                                className={`tw-w-full tw-px-3 tw-py-2 tw-border tw-rounded-lg tw-text-sm focus:tw-outline-none ${amcSubmitAttempted && amcRequiredErrors.amcStartDate ? 'tw-border-red-500 focus:tw-border-red-500' : 'tw-border-gray-200 focus:tw-border-teal-500'}`}
                                            />
                                            {amcSubmitAttempted && amcRequiredErrors.amcStartDate && <p className="tw-text-xs tw-text-red-500 tw-mt-1">AMC Start Date is required</p>}
                                        </div>
                                        <div>
                                            <label className="tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1 tw-block">AMC End Date *</label>
                                            <input
                                                type="date"
                                                value={amcForm.amcEndDate}
                                                onChange={(e) => handleAmcFieldChange('amcEndDate', e.target.value)}
                                                className={`tw-w-full tw-px-3 tw-py-2 tw-border tw-rounded-lg tw-text-sm focus:tw-outline-none ${amcSubmitAttempted && amcRequiredErrors.amcEndDate ? 'tw-border-red-500 focus:tw-border-red-500' : 'tw-border-gray-200 focus:tw-border-teal-500'}`}
                                            />
                                            {amcSubmitAttempted && amcRequiredErrors.amcEndDate && <p className="tw-text-xs tw-text-red-500 tw-mt-1">AMC End Date is required</p>}
                                        </div>
                                        <div>
                                            <label className="tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1 tw-block">AMC Expiry Reminder (No. of Days Prior)</label>
                                            <input
                                                type="number"
                                                min="0"
                                                value={amcForm.expiryReminderDays}
                                                onChange={(e) => handleAmcFieldChange('expiryReminderDays', e.target.value)}
                                                placeholder="e.g. 30"
                                                className="tw-w-full tw-px-3 tw-py-2 tw-border tw-border-gray-200 tw-rounded-lg tw-text-sm focus:tw-outline-none focus:tw-border-teal-500"
                                            />
                                        </div>
                                        <div>
                                            <label className="tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1 tw-block">AMC Service Reminder (No. of Days Prior)</label>
                                            <input
                                                type="number"
                                                min="0"
                                                value={amcForm.serviceReminderDays}
                                                onChange={(e) => handleAmcFieldChange('serviceReminderDays', e.target.value)}
                                                placeholder="e.g. 7"
                                                className="tw-w-full tw-px-3 tw-py-2 tw-border tw-border-gray-200 tw-rounded-lg tw-text-sm focus:tw-outline-none focus:tw-border-teal-500"
                                            />
                                        </div>
                                        <div className="md:tw-col-span-2">
                                            <label className="tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1 tw-block">Description</label>
                                            <textarea
                                                rows="2"
                                                value={amcForm.description}
                                                onChange={(e) => handleAmcFieldChange('description', e.target.value)}
                                                placeholder="Describe the AMC scope or services..."
                                                className="tw-w-full tw-px-3 tw-py-2 tw-border tw-border-gray-200 tw-rounded-lg tw-text-sm focus:tw-outline-none focus:tw-border-teal-500 tw-resize-none"
                                            />
                                        </div>
                                    </div>

                                    {/* Choose Members */}
                                    <div className="tw-border-t tw-border-gray-100 tw-pt-4">
                                        <p className="tw-text-xs tw-font-semibold tw-text-gray-700 tw-mb-3 tw-flex tw-items-center tw-gap-2">
                                            <Icon icon="mdi:bell-ring-outline" className="tw-text-teal-500" />
                                            Choose Members To Send AMC Expiry Email And SMS Reminder
                                        </p>

                                        {/* Tab Toggle */}
                                        <div className="tw-flex tw-gap-2 tw-mb-3">
                                            {['employee', 'committee'].map(mType => (
                                                <button
                                                    key={mType}
                                                    type="button"
                                                    onClick={() => setAmcMemberForm(prev => ({ ...prev, memberType: mType }))}
                                                    className={`tw-px-3 tw-py-1.5 tw-rounded-md tw-text-xs tw-font-medium tw-transition-all ${amcMemberForm.memberType === mType ? 'tw-bg-teal-600 tw-text-white' : 'tw-bg-gray-100 tw-text-gray-600 hover:tw-bg-gray-200'}`}
                                                >
                                                    {mType === 'employee' ? 'Employee' : 'Managing Committees'}
                                                </button>
                                            ))}
                                        </div>

                                        {/* Employee Row */}
                                        {amcMemberForm.memberType === 'employee' && (
                                            <div className="tw-grid tw-grid-cols-3 tw-gap-3 tw-items-end">
                                                <div>
                                                    <label className="tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1 tw-block">Employee Type</label>
                                                    {(() => {
                                                        // Derive the type list from the actual employees we have —
                                                        // the previous hardcoded ["Regular","Contractual","Part-Time"]
                                                        // never matched anything in employee_roles (which is
                                                        // "Security", "Housekeeping", "Manager", …).
                                                        const all = Array.isArray(employeesDropdownData) ? employeesDropdownData : [];
                                                        const types = Array.from(new Set(all.map(e => e.employee_type).filter(Boolean))).sort();
                                                        return (
                                                            <select
                                                                value={amcMemberForm.employeeType}
                                                                onChange={(e) => setAmcMemberForm(prev => ({ ...prev, employeeType: e.target.value, employee: '' }))}
                                                                disabled={employeesDropdownLoading}
                                                                className="tw-w-full tw-px-3 tw-py-2 tw-border tw-border-gray-200 tw-rounded-lg tw-text-sm tw-bg-white focus:tw-outline-none focus:tw-border-teal-500 disabled:tw-bg-gray-50"
                                                            >
                                                                <option value="">
                                                                    {employeesDropdownLoading ? 'Loading…' : types.length === 0 ? 'No employees found' : 'Select Employee Type'}
                                                                </option>
                                                                {types.map(t => (
                                                                    <option key={t} value={t}>{t}</option>
                                                                ))}
                                                            </select>
                                                        );
                                                    })()}
                                                </div>
                                                <div>
                                                    <label className="tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1 tw-block">Employee</label>
                                                    {(() => {
                                                        // Filter the fetched dropdown by the selected type. If no type is
                                                        // picked yet the select is disabled anyway, so `filtered` stays [].
                                                        const all = Array.isArray(employeesDropdownData) ? employeesDropdownData : [];
                                                        const filtered = amcMemberForm.employeeType
                                                            ? all.filter(emp => (emp.employee_type || '').toLowerCase() === amcMemberForm.employeeType.toLowerCase())
                                                            : [];
                                                        return (
                                                            <select
                                                                value={amcMemberForm.employee}
                                                                onChange={(e) => setAmcMemberForm(prev => ({ ...prev, employee: e.target.value }))}
                                                                disabled={!amcMemberForm.employeeType || employeesDropdownLoading}
                                                                className="tw-w-full tw-px-3 tw-py-2 tw-border tw-border-gray-200 tw-rounded-lg tw-text-sm tw-bg-white focus:tw-outline-none focus:tw-border-teal-500 disabled:tw-bg-gray-50 disabled:tw-text-gray-400"
                                                            >
                                                                <option value="">
                                                                    {employeesDropdownLoading
                                                                        ? 'Loading employees…'
                                                                        : !amcMemberForm.employeeType
                                                                            ? 'Select Employee Type first'
                                                                            : filtered.length === 0
                                                                                ? `No ${amcMemberForm.employeeType} employees`
                                                                                : 'Select Employee'}
                                                                </option>
                                                                {filtered.map(emp => {
                                                                    const label = [emp.first_name, emp.last_name].filter(Boolean).join(' ').trim() || emp.email || `Employee #${emp.employee_id}`;
                                                                    return (
                                                                        <option key={emp.employee_id} value={emp.employee_id}>
                                                                            {label}
                                                                        </option>
                                                                    );
                                                                })}
                                                            </select>
                                                        );
                                                    })()}
                                                </div>
                                                <button
                                                    type="button"
                                                    onClick={handleAddAmcMember}
                                                    disabled={!amcMemberForm.employeeType || !amcMemberForm.employee}
                                                    className="tw-flex tw-items-center tw-gap-1.5 tw-bg-teal-600 tw-text-white tw-px-4 tw-py-2 tw-rounded-lg tw-text-sm tw-font-medium hover:tw-bg-teal-700 disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
                                                >
                                                    <Icon icon="mdi:plus" /> Add
                                                </button>
                                            </div>
                                        )}

                                        {/* Committee Row */}
                                        {amcMemberForm.memberType === 'committee' && (
                                            <div className="tw-grid tw-grid-cols-3 tw-gap-3 tw-items-end">
                                                <div>
                                                    <label className="tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1 tw-block">Managing Committees Type</label>
                                                    <select
                                                        value={amcMemberForm.committeeType}
                                                        onChange={(e) => setAmcMemberForm(prev => ({ ...prev, committeeType: e.target.value, committeeMember: '' }))}
                                                        className="tw-w-full tw-px-3 tw-py-2 tw-border tw-border-gray-200 tw-rounded-lg tw-text-sm tw-bg-white focus:tw-outline-none focus:tw-border-teal-500"
                                                    >
                                                        <option value="">Select Committee Type</option>
                                                        <option value="Executive">Executive Committee</option>
                                                        <option value="Finance">Finance Committee</option>
                                                        <option value="Maintenance">Maintenance Committee</option>
                                                        <option value="Security">Security Committee</option>
                                                    </select>
                                                </div>
                                                <div>
                                                    <label className="tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1 tw-block">Managing Committees Member</label>
                                                    <select
                                                        value={amcMemberForm.committeeMember}
                                                        onChange={(e) => setAmcMemberForm(prev => ({ ...prev, committeeMember: e.target.value }))}
                                                        disabled={!amcMemberForm.committeeType}
                                                        className="tw-w-full tw-px-3 tw-py-2 tw-border tw-border-gray-200 tw-rounded-lg tw-text-sm tw-bg-white focus:tw-outline-none focus:tw-border-teal-500 disabled:tw-bg-gray-50 disabled:tw-text-gray-400"
                                                    >
                                                        <option value="">Select Member</option>
                                                    </select>
                                                </div>
                                                <button
                                                    type="button"
                                                    onClick={handleAddAmcMember}
                                                    disabled={!amcMemberForm.committeeType || !amcMemberForm.committeeMember}
                                                    className="tw-flex tw-items-center tw-gap-1.5 tw-bg-teal-600 tw-text-white tw-px-4 tw-py-2 tw-rounded-lg tw-text-sm tw-font-medium hover:tw-bg-teal-700 disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
                                                >
                                                    <Icon icon="mdi:plus" /> Add
                                                </button>
                                            </div>
                                        )}

                                        {/* Members list */}
                                        {amcMembers.length > 0 && (
                                            <div className="tw-mt-3 tw-space-y-1.5">
                                                {amcMembers.map((member, index) => {
                                                    // Resolve the employee id back to a display name from the
                                                    // fetched dropdown. Falls back to whatever the row stores
                                                    // (raw id or a name already saved server-side) so we don't
                                                    // regress hydrated edit-existing-AMC rows.
                                                    let employeeLabel = member.employeeName || member.employee;
                                                    if (member.type === 'employee' && employeesDropdownData) {
                                                        const match = employeesDropdownData.find(
                                                            e => String(e.employee_id) === String(member.employee)
                                                        );
                                                        if (match) {
                                                            employeeLabel = [match.first_name, match.last_name].filter(Boolean).join(' ').trim() || match.email || employeeLabel;
                                                        }
                                                    }
                                                    return (
                                                    <div key={index} className="tw-flex tw-items-center tw-justify-between tw-px-3 tw-py-2 tw-bg-teal-50 tw-border tw-border-teal-100 tw-rounded-lg">
                                                        <div className="tw-flex tw-items-center tw-gap-2 tw-text-xs tw-text-teal-800">
                                                            <Icon icon={member.type === 'employee' ? 'mdi:account' : 'mdi:account-group'} className="tw-text-teal-600" />
                                                            {member.type === 'employee'
                                                                ? <span><span className="tw-font-medium">{member.employeeType}</span> — {employeeLabel}</span>
                                                                : <span><span className="tw-font-medium">{member.committeeType}</span> — {member.committeeMember}</span>
                                                            }
                                                        </div>
                                                        <button
                                                            type="button"
                                                            onClick={() => handleRemoveAmcMember(index)}
                                                            className="tw-p-1 tw-rounded hover:tw-bg-red-100 tw-text-gray-400 hover:tw-text-red-500"
                                                        >
                                                            <Icon icon="mdi:close" className="tw-text-sm" />
                                                        </button>
                                                    </div>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </div>
                                </>
                            )}
                        </div>

                        {/* Footer */}
                        <div className="tw-px-5 tw-py-3 tw-border-t tw-border-gray-100 tw-flex tw-justify-end tw-gap-2">
                            {(amcViewMode === 'add' || amcViewMode === 'edit') && (
                                <button
                                    onClick={() => { setAmcViewMode('list'); setEditingAmcId(null); setAmcSubmitAttempted(false); }}
                                    className="tw-px-4 tw-py-1.5 tw-text-sm tw-font-medium tw-text-gray-600 tw-bg-gray-100 hover:tw-bg-gray-200 tw-rounded-lg tw-flex tw-items-center tw-gap-1.5"
                                >
                                    <Icon icon="mdi:arrow-left" />
                                    Back to List
                                </button>
                            )}
                            <button
                                onClick={handleAmcClose}
                                className="tw-px-4 tw-py-1.5 tw-text-sm tw-font-medium tw-text-gray-600 tw-bg-gray-100 hover:tw-bg-gray-200 tw-rounded-lg"
                            >
                                Close
                            </button>
                            {(amcViewMode === 'add' || amcViewMode === 'edit') && (
                                <button
                                    onClick={handleAmcSave}
                                    disabled={createVendorAmcLoading || updateVendorAmcLoading}
                                    className="tw-px-4 tw-py-1.5 tw-text-sm tw-font-medium tw-text-white tw-bg-teal-600 hover:tw-bg-teal-700 tw-rounded-lg tw-flex tw-items-center tw-gap-1.5 disabled:tw-opacity-50"
                                >
                                    <Icon icon={(createVendorAmcLoading || updateVendorAmcLoading) ? "mdi:loading" : "mdi:content-save-outline"} className={(createVendorAmcLoading || updateVendorAmcLoading) ? "tw-animate-spin" : ""} />
                                    {(createVendorAmcLoading || updateVendorAmcLoading) ? 'Saving...' : amcViewMode === 'edit' ? 'Update AMC' : 'Save AMC'}
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Excel Upload — picker modal */}
            {uploadModalOpen && (
                <div className="tw-fixed tw-inset-0 tw-bg-black/40 tw-flex tw-items-center tw-justify-center tw-z-50 tw-p-4">
                    <div className="tw-bg-white tw-rounded-lg tw-shadow-xl tw-w-full tw-max-w-md">
                        <div className="tw-flex tw-items-center tw-justify-between tw-px-4 tw-py-3 tw-border-b tw-border-gray-100">
                            <h3 className="tw-text-sm tw-font-semibold tw-text-gray-800 tw-flex tw-items-center tw-gap-2">
                                <Icon icon="mdi:file-excel-outline" className="tw-text-emerald-600" />
                                Bulk Upload Vendors
                            </h3>
                            <button
                                onClick={() => { setUploadModalOpen(false); setUploadFile(null); }}
                                className="tw-p-1 tw-rounded hover:tw-bg-gray-100 tw-text-gray-500"
                            >
                                <Icon icon="mdi:close" />
                            </button>
                        </div>
                        <div className="tw-p-4 tw-space-y-3">
                            <p className="tw-text-xs tw-text-gray-600">
                                Upload an Excel file with one vendor per row. Required columns are marked with *.
                                Categories are matched by name (case-insensitive).
                            </p>
                            <button
                                type="button"
                                onClick={handleDownloadTemplate}
                                className="tw-inline-flex tw-items-center tw-gap-1 tw-text-xs tw-text-blue-700 hover:tw-underline"
                            >
                                <Icon icon="mdi:download" /> Download template
                            </button>
                            <label className="tw-block">
                                <span className="tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1 tw-block">Excel file</span>
                                <input
                                    type="file"
                                    accept=".xlsx,.xls"
                                    onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                                    className="tw-block tw-w-full tw-text-xs tw-text-gray-700 file:tw-mr-3 file:tw-py-1.5 file:tw-px-3 file:tw-rounded file:tw-border-0 file:tw-bg-blue-50 file:tw-text-blue-700 hover:file:tw-bg-blue-100"
                                />
                                {uploadFile && (
                                    <p className="tw-text-[11px] tw-text-gray-500 tw-mt-1">
                                        Selected: {uploadFile.name} ({Math.round(uploadFile.size / 1024)} KB)
                                    </p>
                                )}
                            </label>
                        </div>
                        <div className="tw-flex tw-items-center tw-justify-end tw-gap-2 tw-px-4 tw-py-3 tw-bg-gray-50 tw-border-t tw-border-gray-100">
                            <button
                                type="button"
                                onClick={() => { setUploadModalOpen(false); setUploadFile(null); }}
                                disabled={bulkUploadVendorsLoading}
                                className="tw-px-3 tw-py-1.5 tw-text-xs tw-font-medium tw-text-gray-600 tw-border tw-border-gray-200 tw-rounded-md hover:tw-bg-gray-100 disabled:tw-opacity-50"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={handleUploadSubmit}
                                disabled={!uploadFile || bulkUploadVendorsLoading}
                                className="tw-inline-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-text-xs tw-font-medium tw-text-white tw-bg-blue-600 hover:tw-bg-blue-700 tw-rounded-md disabled:tw-opacity-50"
                            >
                                {bulkUploadVendorsLoading && <Icon icon="mdi:loading" className="tw-animate-spin" />}
                                {bulkUploadVendorsLoading ? 'Uploading…' : 'Upload'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Excel Upload — results modal (created / failed lists) */}
            {uploadResultOpen && bulkUploadVendorsResult && (
                <div className="tw-fixed tw-inset-0 tw-bg-black/40 tw-flex tw-items-center tw-justify-center tw-z-50 tw-p-4">
                    <div className="tw-bg-white tw-rounded-lg tw-shadow-xl tw-w-full tw-max-w-2xl tw-max-h-[85vh] tw-flex tw-flex-col">
                        <div className="tw-flex tw-items-center tw-justify-between tw-px-4 tw-py-3 tw-border-b tw-border-gray-100">
                            <h3 className="tw-text-sm tw-font-semibold tw-text-gray-800 tw-flex tw-items-center tw-gap-2">
                                <Icon icon="mdi:file-check-outline" className="tw-text-emerald-600" />
                                Upload Summary
                            </h3>
                            <button
                                onClick={handleCloseResults}
                                className="tw-p-1 tw-rounded hover:tw-bg-gray-100 tw-text-gray-500"
                            >
                                <Icon icon="mdi:close" />
                            </button>
                        </div>
                        <div className="tw-p-4 tw-overflow-y-auto tw-flex-1">
                            <div className="tw-grid tw-grid-cols-3 tw-gap-2 tw-mb-4">
                                <div className="tw-rounded-md tw-bg-blue-50 tw-border tw-border-blue-100 tw-px-3 tw-py-2">
                                    <div className="tw-text-[10px] tw-text-blue-700 tw-uppercase tw-tracking-wide tw-font-semibold">Total</div>
                                    <div className="tw-text-lg tw-font-bold tw-text-blue-900">{bulkUploadVendorsResult.total}</div>
                                </div>
                                <div className="tw-rounded-md tw-bg-emerald-50 tw-border tw-border-emerald-100 tw-px-3 tw-py-2">
                                    <div className="tw-text-[10px] tw-text-emerald-700 tw-uppercase tw-tracking-wide tw-font-semibold">Created</div>
                                    <div className="tw-text-lg tw-font-bold tw-text-emerald-900">{bulkUploadVendorsResult.createdCount}</div>
                                </div>
                                <div className="tw-rounded-md tw-bg-rose-50 tw-border tw-border-rose-100 tw-px-3 tw-py-2">
                                    <div className="tw-text-[10px] tw-text-rose-700 tw-uppercase tw-tracking-wide tw-font-semibold">Failed</div>
                                    <div className="tw-text-lg tw-font-bold tw-text-rose-900">{bulkUploadVendorsResult.failedCount}</div>
                                </div>
                            </div>

                            {bulkUploadVendorsResult.created?.length > 0 && (
                                <div className="tw-mb-4">
                                    <h4 className="tw-text-xs tw-font-semibold tw-text-emerald-700 tw-uppercase tw-tracking-wide tw-mb-1.5">Created</h4>
                                    <ul className="tw-space-y-0.5 tw-text-xs tw-text-gray-700 tw-max-h-40 tw-overflow-y-auto tw-border tw-border-gray-100 tw-rounded tw-p-2">
                                        {bulkUploadVendorsResult.created.map(r => (
                                            <li key={r.vendor_id} className="tw-flex tw-items-center tw-gap-2">
                                                <span className="tw-text-gray-400 tw-font-mono tw-text-[10px] tw-w-8">#{r.row}</span>
                                                <Icon icon="mdi:check-circle" className="tw-text-emerald-500 tw-text-sm" />
                                                <span>{r.company_name}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}

                            {bulkUploadVendorsResult.failed?.length > 0 && (
                                <div>
                                    <h4 className="tw-text-xs tw-font-semibold tw-text-rose-700 tw-uppercase tw-tracking-wide tw-mb-1.5">Failed</h4>
                                    <ul className="tw-space-y-0.5 tw-text-xs tw-text-gray-700 tw-max-h-56 tw-overflow-y-auto tw-border tw-border-rose-100 tw-rounded tw-p-2">
                                        {bulkUploadVendorsResult.failed.map((r, i) => (
                                            <li key={i} className="tw-flex tw-items-start tw-gap-2">
                                                <span className="tw-text-gray-400 tw-font-mono tw-text-[10px] tw-w-8 tw-mt-0.5">#{r.row}</span>
                                                <Icon icon="mdi:alert-circle" className="tw-text-rose-500 tw-text-sm tw-mt-0.5 tw-flex-shrink-0" />
                                                <span>
                                                    {r.company_name && <span className="tw-font-medium">{r.company_name}: </span>}
                                                    <span className="tw-text-rose-700">{r.error}</span>
                                                </span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </div>
                        <div className="tw-flex tw-items-center tw-justify-end tw-px-4 tw-py-3 tw-bg-gray-50 tw-border-t tw-border-gray-100">
                            <button
                                onClick={handleCloseResults}
                                className="tw-px-3 tw-py-1.5 tw-text-xs tw-font-medium tw-text-white tw-bg-blue-600 hover:tw-bg-blue-700 tw-rounded-md"
                            >
                                Done
                            </button>
                        </div>
                    </div>
                </div>
            )}

        </>
    );
};

export default Vendors;
